import { defineStore } from "pinia";
import { auth, db } from "@/firebase/config";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  updateProfile,
} from "firebase/auth";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";

export const useAuthStore = defineStore("auth", {
  state: () => ({
    user: null,
    userProfile: null,
    claimsRole: null,
    loading: false,
    error: null,
    authReady: false,
  }),

  getters: {
    // Custom Claims are the only source of truth for authorization
    userRole: (state) => state.claimsRole,
    isAdmin: (state) => {
      return state.claimsRole === "admin" || state.claimsRole === "super_admin";
    },
    isModerator: (state) => {
      return ["moderator", "admin", "super_admin"].includes(state.claimsRole);
    },
    isSuperAdmin: (state) => state.claimsRole === "super_admin",
    isPending: (state) =>
      state.userProfile?.firestoreRole === "pending" &&
      !["moderator", "admin", "super_admin"].includes(state.claimsRole),
  },

  actions: {
    /**
     * Always force-refresh the ID token so Custom Claims stay current.
     * Do not rely on the user to log out after a role change.
     */
    async refreshClaims() {
      const currentUser = this.user || auth.currentUser;
      if (!currentUser) {
        this.claimsRole = null;
        return null;
      }

      try {
        const tokenResult = await currentUser.getIdTokenResult(true);
        this.user = currentUser;
        this.claimsRole = tokenResult.claims.role || "user";

        if (this.userProfile) {
          this.userProfile = {
            ...this.userProfile,
            role: this.claimsRole,
          };
        }

        return this.claimsRole;
      } catch (error) {
        console.error("Error refreshing custom claims:", error);
        this.claimsRole = null;
        throw error;
      }
    },

    async checkToken() {
      try {
        await this.refreshClaims();
      } catch (error) {
        console.error("Error checking token:", error);
        await signOut(auth);
        this.user = null;
        this.userProfile = null;
        this.claimsRole = null;
      }
    },

    async init() {
      try {
        return new Promise((resolve) => {
          const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            if (currentUser) {
              this.user = currentUser;
              try {
                await this.refreshClaims();
                await this.loadUserProfile(currentUser.uid);
              } catch (error) {
                console.error("Error loading user profile:", error);
                this.userProfile = null;
                this.claimsRole = null;
              }
            } else {
              this.user = null;
              this.userProfile = null;
              this.claimsRole = null;
            }
            this.authReady = true;
            unsubscribe();
            resolve();
          });
        });
      } catch (error) {
        console.error("Auth initialization error:", error);
        this.error = "Authentication failed to initialize";
        throw error;
      }
    },

    async signup(email, password, displayName) {
      this.loading = true;
      this.error = null;

      try {
        const userCredential = await createUserWithEmailAndPassword(
          auth,
          email,
          password
        );

        // Create user profile with pending role
        await setDoc(doc(db, "users", userCredential.user.uid), {
          uid: userCredential.user.uid,
          email,
          displayName,
          role: "pending",
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          lastLoginAt: serverTimestamp(),
        });

        // Update display name in Firebase Auth
        await updateProfile(userCredential.user, {
          displayName,
        });

        // Load user profile
        this.user = userCredential.user;
        await this.refreshClaims();
        await this.loadUserProfile(userCredential.user.uid);

        return userCredential.user;
      } catch (error) {
        console.error("Signup error:", error);
        this.error = error.message;
        throw error;
      } finally {
        this.loading = false;
      }
    },

    // Sign in with Google
    async signInWithGoogle() {
      this.loading = true;
      this.error = null;
      try {
        const provider = new GoogleAuthProvider();
        provider.setCustomParameters({
          prompt: "select_account",
        });

        const result = await signInWithPopup(auth, provider);

        // Start with reading the user document - this is safer
        const userRef = doc(db, "users", result.user.uid);
        let userData = null;

        try {
          const userDoc = await getDoc(userRef);

          if (!userDoc.exists()) {
            console.log("Creating new user document for Google sign-in");
            userData = {
              uid: result.user.uid,
              email: result.user.email,
              displayName: result.user.displayName,
              photoURL: result.user.photoURL,
              role: "pending",
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
              lastLoginAt: serverTimestamp(),
            };

            await setDoc(userRef, userData);
          } else {
            console.log("Updating existing user document");
            userData = userDoc.data();

            // Update timestamps
            await setDoc(
              userRef,
              {
                lastLoginAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
              },
              { merge: true }
            );
          }
        } catch (firestoreError) {
          console.error(
            "Firestore error during Google sign-in:",
            firestoreError
          );
          throw new Error(`Firestore error: ${firestoreError.message}`);
        }

        // Load the user profile after database operations
        this.user = result.user;
        await this.refreshClaims();
        await this.loadUserProfile(result.user.uid);

        return result;
      } catch (error) {
        console.error("Google sign-in error:", error);
        if (error.code === "auth/popup-closed-by-user") {
          this.error = "Sign in was cancelled";
        } else if (error.code === "auth/popup-blocked") {
          this.error = "Popup was blocked by the browser";
        } else if (error.code && error.code.startsWith("auth/")) {
          this.error = error.code;
        } else {
          this.error = `Error: ${error.message}`;
        }
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async login(email, password) {
      this.loading = true;
      this.error = null;
      try {
        console.log("Starting login process...");
        const { user } = await signInWithEmailAndPassword(
          auth,
          email,
          password
        );

        // Check if user exists and has proper role
        const userDoc = await getDoc(doc(db, "users", user.uid));

        if (userDoc.exists()) {
          console.log("User doc exists:", userDoc.data());
        }

        if (!userDoc.exists()) {
          await signOut(auth);
          throw new Error("Access denied. Please contact administrator.");
        }

        await setDoc(
          doc(db, "users", user.uid),
          {
            lastLoginAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );

        this.user = user;
        await this.refreshClaims();
        await this.loadUserProfile(user.uid);
        return user;
      } catch (error) {
        console.error("Login failed:", error);
        this.error = error.message;
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async loadUserProfile(uid) {
      try {
        const docRef = doc(db, "users", uid);
        const docSnap = await getDoc(docRef);

        let userData = null;
        if (docSnap.exists()) {
          userData = docSnap.data();
        } else {
          console.warn("No user profile found in Firestore!");
        }

        if (!this.user && auth.currentUser) {
          this.user = auth.currentUser;
        }

        const claimRole = this.claimsRole || (await this.refreshClaims()) || "user";

        this.userProfile = {
          id: uid,
          ...userData,
          firestoreRole: userData?.role || "user",
          role: claimRole,
          createdAt: userData?.createdAt,
          updatedAt: userData?.updatedAt,
          lastLoginAt: userData?.lastLoginAt,
        };
      } catch (error) {
        console.error("Error loading user profile:", error);
        this.error = error.message;
      }
    },

    async logout() {
      try {
        await signOut(auth);
        this.user = null;
        this.userProfile = null;
        this.claimsRole = null;
      } catch (error) {
        this.error = error.message;
        throw error;
      }
    },

    checkPermission() {
      return true;
    },

    debugAuthState() {
      console.log({
        user: this.user,
        userProfile: this.userProfile,
        claimsRole: this.claimsRole,
        isAdmin: this.isAdmin,
        isModerator: this.isModerator,
        isSuperAdmin: this.isSuperAdmin,
        authReady: this.authReady,
      });
    },

    async fetchUserProfile(uid) {
      await this.loadUserProfile(uid);
    },
  },
});
